import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { calculateBodyMetrics, saveCalculatorResults } from '../services/api';
import { SaveCalculatorButton } from './SaveCalculatorButton';
import { 
  Calculator, Activity, Flame, Percent, ArrowRight, RotateCcw, 
  TrendingDown, TrendingUp, Minus, Mail, CheckCircle2, BookOpen 
} from 'lucide-react';
import { toast } from 'sonner';

const initialForm = {
  gender: 'male',
  age: '',
  weight_kg: '',
  height_cm: '',
  neck_cm: '',
  waist_cm: '',
  hip_cm: '',
  activity_level: 'moderate',
  goal: 'maintenance',
};

const activityOptions = [
  { value: 'sedentary', label: 'Ülőmunka', description: 'Irodai munka, minimális napi mozgás, heti 0–1 edzés' },
  { value: 'moderate',  label: 'Mérsékelt aktivitás', description: 'Ülőmunka mellett heti 3–4 súlyzós vagy kardió edzés' },
  { value: 'intense',   label: 'Intenzív aktivitás', description: 'Fizikai munka / műszakos állómunka, vagy heti 5+ kemény edzés' }
];

const goalOptions = [
  { value: 'fat_loss',    label: 'Fogyás / Szálkásítás', icon: TrendingDown, hint: '-500 kcal' },
  { value: 'maintenance', label: 'Szintentartás',         icon: Minus,        hint: 'TDEE' },
  { value: 'muscle_gain', label: 'Tömegelés / Izomépítés', icon: TrendingUp,  hint: '+300 kcal' },
];

const goalLabelByValue = {
  fat_loss: 'Fogyás / Szálkásítás',
  maintenance: 'Szintentartás',
  muscle_gain: 'Tömegelés / Izomépítés',
};

const CalculatorSection = ({ onBookClick, defaultGender }) => {
  const [form, setForm] = useState(() => ({ ...initialForm, gender: defaultGender || initialForm.gender }));
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [saveEmail, setSaveEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = (overrides = {}) => {
    const payload = {
      gender: form.gender,
      activity_level: form.activity_level,
      goal: form.goal,
      age: parseInt(form.age, 10),
      weight_kg: parseFloat(form.weight_kg),
      height_cm: parseFloat(form.height_cm),
      neck_cm: parseFloat(form.neck_cm),
      waist_cm: parseFloat(form.waist_cm),
      ...overrides,
    };
    if (form.gender === 'female') {
      payload.hip_cm = parseFloat(form.hip_cm);
    }
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsCalculating(true);

    try {
      const data = await calculateBodyMetrics(buildPayload());
      setResult(data);
      setIsSaved(false);

      setTimeout(() => {
        const el = document.getElementById('calculator-results');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'A számítás nem sikerült. Ellenőrizd az adatokat.';
      toast.error(typeof msg === 'string' ? msg : 'Hibás bemenet');
    } finally {
      setIsCalculating(false);
    }
  };

  // When user switches goal AFTER calculation, re-run the calc on the backend so target is fresh
  const handleGoalChange = async (newGoal) => {
    handleChange('goal', newGoal);
    if (!result) return;
    try {
      const data = await calculateBodyMetrics(buildPayload({ goal: newGoal }));
      setResult(data);
    } catch (error) {
      // silent: form may have changed in the meantime
    }
  };

  const handleReset = () => {
    setResult(null);
    setSaveEmail('');
    setIsSaved(false);
    setForm(initialForm);
  };

  const handleSaveResults = async (e) => {
    e.preventDefault();
    if (!result || !saveEmail) return;
    setIsSaving(true);
    try {
      const payload = {
        email: saveEmail,
        snapshot: buildPayload(),
        result,
      };
      await saveCalculatorResults(payload);
      setIsSaved(true);
      toast.success('Az eredményeid mentve! Hamarosan elküldjük a kiértékelést.');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'A mentés nem sikerült. Próbáld újra.';
      toast.error(typeof msg === 'string' ? msg : 'Mentési hiba');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section 
      id="calculator-testzsir-tdee" 
      data-testid="calculator-section"
      className="py-24 bg-gradient-to-b from-gray-900 to-gray-950 relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }}></div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-yellow-600/20 border border-yellow-600/30 rounded-full">
            <Calculator className="text-yellow-400" size={16} />
            <span className="text-yellow-400 font-semibold text-sm tracking-wider">TUDOMÁNYOS KALKULÁTOR</span>
          </div>

          <h2 
            data-testid="calculator-heading"
            className="text-4xl md:text-5xl font-bold text-white mb-5"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Ismerd meg
            <span className="text-yellow-400"> a számaidat</span>
          </h2>

          <p className="text-lg text-gray-300 leading-relaxed">
            Add meg a pontos adataidat, és számold ki a becsült testzsírszázalékodat, 
            valamint a napi kalóriaszükségletedet a céljaidhoz mérten.
          </p>
        </div>

        {/* Form */}
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl p-5 sm:p-8 md:p-10">
          {/* Why this beats plain BMI — links to the US Navy article */}
          <a
            href="/tudastar/mennyire-pontos-a-us-navy-testzsirszazalek-kalkulator-es-hogyan-merd-meg-magad"
            data-testid="calculator-bmi-article-link"
            className="group flex items-start gap-3 mb-6 p-4 rounded-xl border border-yellow-600/30 bg-gradient-to-br from-yellow-50 to-white hover:border-yellow-600 hover:shadow-md transition-all"
          >
            <span className="flex-shrink-0 mt-0.5 flex items-center justify-center w-9 h-9 rounded-lg bg-yellow-600/10 text-yellow-700">
              <BookOpen size={18} />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-bold text-gray-900 leading-snug break-words">
                Miért pontosabb ez, mint a sima BMI?
              </span>
              <span className="block text-xs text-gray-600 leading-relaxed mt-1 break-words">
                A BMI csak a testsúlyt és a magasságot ismeri — nem tudja, hogy a kilóid zsírból vagy izomból
                vannak. A US Navy kerületi módszer ezt szétválasztja. Elmagyarázom a cikkben, mennyire pontos
                és hogyan mérj otthon helyesen.
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-yellow-700 mt-2 group-hover:gap-2 transition-all">
                Elolvasom a cikket <ArrowRight size={13} />
              </span>
            </span>
          </a>

          <form onSubmit={handleSubmit} data-testid="calculator-form" className="space-y-6">
            {/* Gender + Activity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="min-w-0">
                <Label className="text-gray-700 font-semibold mb-2 block">Nem</Label>
                <div data-testid="calculator-gender-toggle" className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                  {[
                    { v: 'male', l: 'Férfi' },
                    { v: 'female', l: 'Nő' },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`calculator-gender-${opt.v}`}
                      onClick={() => handleChange('gender', opt.v)}
                      className={`flex-1 px-4 py-2.5 text-sm font-semibold rounded-md transition-all ${
                        form.gender === opt.v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >{opt.l}</button>
                  ))}
                </div>
              </div>

              <div className="min-w-0">
                <Label htmlFor="activity" className="text-gray-700 font-semibold mb-2 block">Aktivítási szint</Label>
                <Select value={form.activity_level} onValueChange={(v) => handleChange('activity_level', v)}>
                  <SelectTrigger data-testid="calculator-activity-select" id="activity" className="h-11 w-full">
                    <SelectValue placeholder="Válassz aktivitási szintet">
                      {activityOptions.find((o) => o.value === form.activity_level)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-w-[min(92vw,520px)]">
                    {activityOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} data-testid={`activity-${opt.value}`} className="py-3 whitespace-normal">
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <span className="font-semibold text-gray-900 break-words">{opt.label}</span>
                          <span className="text-xs text-gray-500 leading-snug break-words">{opt.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Goal selector */}
            <div>
              <Label className="text-gray-700 font-semibold mb-2 block">Cél</Label>
              <div data-testid="calculator-goal-toggle" className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {goalOptions.map((opt) => {
                  const Icon = opt.icon;
                  const isActive = form.goal === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`calculator-goal-${opt.value}`}
                      onClick={() => handleGoalChange(opt.value)}
                      className={`group flex items-center gap-3 p-3 rounded-lg border-2 transition-all duration-200 ${
                        isActive 
                          ? 'border-yellow-600 bg-yellow-50' 
                          : 'border-gray-200 hover:border-yellow-400 bg-white'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-9 h-9 rounded-md transition-colors ${
                        isActive ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Icon size={18} />
                      </div>
                      <div className="text-left flex-1">
                        <div className={`text-sm font-semibold ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                          {opt.label}
                        </div>
                        <div className={`text-xs ${isActive ? 'text-yellow-700' : 'text-gray-500'}`}>
                          {opt.hint}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Age + Weight + Height */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div>
                <Label htmlFor="age" className="text-gray-700 font-semibold mb-2 block">Életkor (év)</Label>
                <Input data-testid="calculator-age-input" id="age" type="number" min="14" max="100" required
                  value={form.age} onChange={(e) => handleChange('age', e.target.value)} placeholder="30" className="h-11"/>
              </div>
              <div>
                <Label htmlFor="weight" className="text-gray-700 font-semibold mb-2 block">Testsúly (kg)</Label>
                <Input data-testid="calculator-weight-input" id="weight" type="number" step="0.1" min="20" max="300" required
                  value={form.weight_kg} onChange={(e) => handleChange('weight_kg', e.target.value)} placeholder="75" className="h-11"/>
              </div>
              <div>
                <Label htmlFor="height" className="text-gray-700 font-semibold mb-2 block">Magasság (cm)</Label>
                <Input data-testid="calculator-height-input" id="height" type="number" step="0.1" min="100" max="250" required
                  value={form.height_cm} onChange={(e) => handleChange('height_cm', e.target.value)} placeholder="180" className="h-11"/>
              </div>
            </div>

            {/* Circumference */}
            <div className={`grid grid-cols-1 gap-5 ${form.gender === 'female' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
              <div>
                <Label htmlFor="neck" className="text-gray-700 font-semibold mb-2 block">Nyak kerülete (cm)</Label>
                <Input data-testid="calculator-neck-input" id="neck" type="number" step="0.1" min="15" max="80" required
                  value={form.neck_cm} onChange={(e) => handleChange('neck_cm', e.target.value)} placeholder="40" className="h-11"/>
              </div>
              <div>
                <Label htmlFor="waist" className="text-gray-700 font-semibold mb-2 block">Derék kerülete (cm)</Label>
                <Input data-testid="calculator-waist-input" id="waist" type="number" step="0.1" min="40" max="200" required
                  value={form.waist_cm} onChange={(e) => handleChange('waist_cm', e.target.value)} placeholder="85" className="h-11"/>
              </div>
              {form.gender === 'female' && (
                <div>
                  <Label htmlFor="hip" className="text-gray-700 font-semibold mb-2 block">Csípő kerülete (cm)</Label>
                  <Input data-testid="calculator-hip-input" id="hip" type="number" step="0.1" min="40" max="200" required
                    value={form.hip_cm} onChange={(e) => handleChange('hip_cm', e.target.value)} placeholder="95" className="h-11"/>
                </div>
              )}
            </div>

            <div className="pt-2">
              <Button data-testid="calculator-submit-btn" type="submit" disabled={isCalculating}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold text-lg py-6 transition-all duration-300">
                {isCalculating ? 'Számolás…' : 'Eredmények kiszámítása'}
              </Button>
              <p className="text-xs text-gray-500 text-center mt-3">
                US Navy kerületi módszer · Mifflin-St Jeor BMR · Csak tájékoztató jellegű becslés.
              </p>
            </div>
          </form>

          {/* Results */}
          {result && (
            <div id="calculator-results" data-testid="calculator-results" className="mt-10 pt-10 border-t border-gray-200">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Az eredményeid
                </h3>
                <div className="flex items-center gap-3 flex-wrap">
                  <SaveCalculatorButton
                    type="testzsir"
                    input={{
                      gender: form.gender, age: form.age, height_cm: form.height_cm,
                      weight_kg: form.weight_kg, waist_cm: form.waist_cm,
                      neck_cm: form.neck_cm, hip_cm: form.hip_cm,
                    }}
                    output={{
                      body_fat_pct: result.body_fat_pct, lean_mass_kg: result.lean_mass_kg,
                      fat_mass_kg: result.fat_mass_kg, bmr_kcal: result.bmr_kcal,
                      target_kcal: result.target_kcal, goal: result.goal,
                      goal_adjustment: result.goal_adjustment, category: result.category,
                    }}
                    testid="calc-save-testzsir"
                  />
                  <button type="button" data-testid="calculator-reset-btn" onClick={handleReset}
                    className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors">
                    <RotateCcw size={14} /> Új számítás
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {/* Body fat */}
                <div data-testid="result-bodyfat-card"
                  className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 rounded-xl border border-gray-800 hover:border-yellow-600/40 transition-all">
                  <div className="flex items-center gap-2 text-yellow-400 text-xs font-bold tracking-widest uppercase mb-3">
                    <Percent size={14} /> Becsült testzsír
                  </div>
                  <div className="text-4xl font-bold mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {result.body_fat_pct.toFixed(1)}<span className="text-2xl text-yellow-400">%</span>
                  </div>
                  <div className="text-sm text-gray-400">Kategória: {result.category}</div>
                </div>

                {/* BMR */}
                <div data-testid="result-bmr-card"
                  className="bg-white border-2 border-gray-100 hover:border-yellow-600 p-6 rounded-xl transition-all">
                  <div className="flex items-center gap-2 text-yellow-600 text-xs font-bold tracking-widest uppercase mb-3">
                    <Activity size={14} /> Alapanyagcsere (BMR)
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {result.bmr_kcal.toLocaleString('hu-HU')}<span className="text-lg text-gray-500 ml-1">kcal</span>
                  </div>
                  <div className="text-sm text-gray-500">Nyugalmi energiaszükséglet</div>
                </div>

                {/* Target (TDEE adjusted by goal) */}
                <div data-testid="result-target-card" className="bg-yellow-50 border-2 border-yellow-600 p-6 rounded-xl">
                  <div className="flex items-center gap-2 text-yellow-700 text-xs font-bold tracking-widest uppercase mb-3">
                    <Flame size={14} /> Napi kalória célod
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {result.target_kcal.toLocaleString('hu-HU')}<span className="text-lg text-gray-500 ml-1">kcal</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    {goalLabelByValue[result.goal]}
                    {result.goal_adjustment !== 0 && (
                      <span className="text-gray-500"> · TDEE {result.goal_adjustment > 0 ? '+' : ''}{result.goal_adjustment} kcal</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Secondary stats */}
              <div className="grid grid-cols-3 gap-4 mb-8 pb-8 border-b border-gray-200">
                <div data-testid="result-fatmass" className="text-center min-w-0">
                  <div className="text-[10px] sm:text-xs font-semibold text-gray-500 tracking-wide sm:tracking-wider uppercase mb-1 break-words">Zsírtömeg</div>
                  <div className="text-lg sm:text-xl font-bold text-gray-900 whitespace-nowrap">{result.fat_mass_kg.toFixed(1)} kg</div>
                </div>
                <div data-testid="result-leanmass" className="text-center min-w-0">
                  <div className="text-[10px] sm:text-xs font-semibold text-gray-500 tracking-wide sm:tracking-wider uppercase mb-1 break-words">Sovány tömeg</div>
                  <div className="text-lg sm:text-xl font-bold text-gray-900 whitespace-nowrap">{result.lean_mass_kg.toFixed(1)} kg</div>
                </div>
                <div data-testid="result-bmi" className="text-center min-w-0">
                  <div className="text-[10px] sm:text-xs font-semibold text-gray-500 tracking-wide sm:tracking-wider uppercase mb-1 break-words">BMI</div>
                  <div className="text-lg sm:text-xl font-bold text-gray-900">{result.bmi.toFixed(1)}</div>
                </div>
              </div>

              {/* Save Results via Email */}
              <div 
                data-testid="calculator-save-block"
                className="mb-8 bg-gradient-to-br from-gray-50 to-yellow-50/40 border border-gray-200 rounded-xl p-6 md:p-8"
              >
                {isSaved ? (
                  <div className="text-center py-4" data-testid="calculator-save-success">
                    <CheckCircle2 className="mx-auto text-green-500 mb-3" size={48} />
                    <h4 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Eredmények elmentve!
                    </h4>
                    <p className="text-gray-700 text-sm">
                      Hamarosan elküldjük a részletes PDF kiértékelést a <span className="font-semibold">{saveEmail}</span> címre.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-3 mb-4">
                      <div className="bg-yellow-600/10 border border-yellow-600/20 p-2 rounded-lg flex-shrink-0">
                        <Mail className="text-yellow-600" size={20} />
                      </div>
                      <div>
                        <h4 className="text-lg md:text-xl font-bold text-gray-900 leading-tight mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          Szeretnéd elmenteni az eredményeidet?
                        </h4>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          Add meg az e-mail címed, és elküldjük a részletes PDF kiértékelést és 
                          a napi kalóriacéledet a postafiókodba!
                        </p>
                      </div>
                    </div>

                    <form onSubmit={handleSaveResults} className="flex flex-col sm:flex-row gap-3" data-testid="calculator-save-form">
                      <Input
                        data-testid="calculator-save-email-input"
                        type="email"
                        required
                        value={saveEmail}
                        onChange={(e) => setSaveEmail(e.target.value)}
                        placeholder="Add meg az e-mail címed..."
                        className="flex-1 h-12 text-base"
                      />
                      <Button
                        data-testid="calculator-save-submit-btn"
                        type="submit"
                        disabled={isSaving}
                        className="bg-gray-900 hover:bg-yellow-600 text-white font-bold h-12 px-6 transition-all duration-300 whitespace-nowrap"
                      >
                        {isSaving ? 'Mentés…' : 'Eredmények mentése'}
                        <Mail className="ml-2" size={18} />
                      </Button>
                    </form>

                    <p className="text-xs text-gray-500 mt-3">
                      Az „Eredmények mentése" gombra kattintva elfogadod az{' '}
                      <a href="/adatkezelesi-tajekoztato" target="_blank" rel="noopener noreferrer" className="underline text-yellow-700 hover:text-yellow-600 font-semibold">adatkezelési tájékoztatót</a>
                      , és hozzájárulsz, hogy az Orosz Coaching automatizált e-maileket küldjön az eredményekről és releváns edzési cikkekről. Bármikor leiratkozhatsz egy kattintással.
                    </p>
                  </>
                )}
              </div>

              {/* CTA Banner */}
              <div data-testid="calculator-cta-banner" className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white rounded-xl p-6 md:p-8">
                <div className="md:flex md:items-center md:justify-between gap-6">
                  <div className="mb-4 md:mb-0">
                    <h4 className="text-xl md:text-2xl font-bold mb-2 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Szeretnéd, hogy ezen adatok alapján milliméter pontos, személyre szabott stratégiát készítsek neked?
                    </h4>
                    <p className="text-yellow-50">Foglalj egy ingyenes konzultációt, vagy válassz programot!</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 md:flex-shrink-0">
                    <button type="button" data-testid="calculator-cta-book" onClick={onBookClick}
                      className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 font-bold px-6 py-3 rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap">
                      Konzultáció foglalása <ArrowRight size={16} />
                    </button>
                    <a href="#programs" data-testid="calculator-cta-programs"
                      className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-bold px-6 py-3 rounded-lg hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap">
                      Programok
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CalculatorSection;
